import { AfterViewInit, Component, EventEmitter, OnInit, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../../../../directives/progressive-typeahead.directive';
import { TextNormalizeDirective } from '../../../../../../directives/text-normalize.directive';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DirectorService } from '../../../../../../services/director.service';
import { DirectorFlat } from '../../../../../../models/director';
import { DirectorsSearchRequest } from '../../../../../../models/api/directorsSearchRequest';
import { LookupDataService } from '../../../../../../services/lookupDataService.service';
import { Nationality } from '../../../../../../models/nationality';
import { Profession } from '../../../../../../models/profession';

@Component({
  selector: 'app-add-board-member-search',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    TextNormalizeDirective,
    NgxSpinnerModule,
    FormsModule
  ],
  templateUrl: './add-board-member-search.component.html',
  styleUrls: ['./add-board-member-search.component.scss']
})
export class AddBoardMemberSearchComponent implements OnInit, AfterViewInit {
  @Output() directorSelected = new EventEmitter<DirectorFlat | null>();
  @Output() addNewRequested = new EventEmitter<void>();
  @Output() editRequested = new EventEmitter<DirectorFlat>();

  private directorService = inject(DirectorService);
  private spinnerService = inject(NgxSpinnerService);
  private lookupDataService = inject(LookupDataService);

  dataSource = new MatTableDataSource<DirectorFlat>([]);
  private allColumns: string[] = ['actions', 'fullName', 'age', 'nationalityName', 'boardMemberships'];
  pageSize: number = 5;
  selectedDirector: DirectorFlat | null = null;
  hasSearched = false;
  showBoardMemberships = false;

  get displayedColumns(): string[] {
    if (this.showBoardMemberships) {
      return this.allColumns;
    }
    return this.allColumns.filter(c => c !== 'boardMemberships');
  }

  nationalities: Nationality[] = [];
  professions: Profession[] = [];

  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  searchForm: {
    fullName: string;
    ageFrom: number | null;
    ageTo: number | null;
    gender: number | null;
    nationalityId: string;
    professionId: string;
  } = {
    fullName: '',
    ageFrom: null,
    ageTo: null,
    gender: null,
    nationalityId: '',
    professionId: ''
  };

  ngOnInit(): void {
    this.loadLookupData();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onSearch(): void {
    this.searchDirectors();
  }

  onClear(): void {
    this.searchForm = {
      fullName: '',
      ageFrom: null,
      ageTo: null,
      gender: null,
      nationalityId: '',
      professionId: ''
    };
    this.dataSource.data = [];
    this.selectedDirector = null;
    this.hasSearched = false;
    this.directorSelected.emit(null);
  }

  get hasSearchCriteria(): boolean {
    return !!(
      this.searchForm.fullName ||
      this.searchForm.ageFrom != null ||
      this.searchForm.ageTo != null ||
      this.searchForm.gender != null ||
      this.searchForm.nationalityId ||
      this.searchForm.professionId
    );
  }

  getDisplayAge(director: DirectorFlat): string {
    if (!director.yearOfBirth) return '';
    let refYear: number;
    if (director.isDeceased && director.dateOfDeath) {
      refYear = new Date(director.dateOfDeath).getFullYear();
    } else {
      refYear = new Date().getFullYear();
    }
    const age = refYear - director.yearOfBirth;
    if (age < 0 || age > 150) return '';
    return `${age}`;
  }

  getBoardMemberships(director: DirectorFlat): { label: string }[] {
    return (director.boardMemberships || [])
      .sort((a, b) => a.companyName.localeCompare(b.companyName) || a.year - b.year)
      .map(m => ({ label: `${m.companyName} (${m.year})` }));
  }

  searchDirectors(): void {
    if (!this.hasSearchCriteria) return;

    const request: DirectorsSearchRequest = {
      fullName: this.searchForm.fullName || undefined,
      ageFrom: this.searchForm.ageFrom ?? undefined,
      ageTo: this.searchForm.ageTo ?? undefined,
      gender: this.searchForm.gender ?? undefined,
      nationalityId: this.searchForm.nationalityId || undefined,
      professionId: this.searchForm.professionId || undefined,
      pageNumber: 1,
      pageSize: 100,
      sortBy: 'FullName',
      ascending: true
    };

    this.spinnerService.show();
    this.directorService.searchDirectors(request).subscribe({
      next: (response) => {
        this.hasSearched = true;
        this.dataSource.data = response.data;
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.selectedDirector = null;
        this.directorSelected.emit(null);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error searching directors:', error);
        this.spinnerService.hide();
      }
    });
  }

  onConnect(director: DirectorFlat): void {
    if (this.selectedDirector?.id === director.id) {
      this.selectedDirector = null;
    } else {
      this.selectedDirector = director;
    }
    this.directorSelected.emit(this.selectedDirector);
  }

  isSelected(director: DirectorFlat): boolean {
    return this.selectedDirector?.id === director.id;
  }

  private loadLookupData(): void {
    this.lookupDataService.getTableStream<Nationality>('nationalities').subscribe(data => {
      if (data) this.nationalities = data;
    });
    this.lookupDataService.getTableStream<Profession>('professions').subscribe(data => {
      if (data) this.professions = data;
    });
  }
}
